import { useEffect, useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { T, Var } from 'gt-react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Badge } from 'src/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import {
  CircleNotchIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  PlugIcon,
  PlugsIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import {
  useGetIntegration,
  useBeginMicrosoftTeamsConsent,
  useTestMicrosoftTeamsConnection,
  useDisconnectMicrosoftTeams,
} from 'src/api/rpc/hooks/integrations/useIntegrations';
import {
  IntegrationAvailability,
  IntegrationKey,
  MicrosoftTeamsIntegrationSettings as TeamsSettings,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';

type ConsentStatus = 'success' | 'cancelled' | 'invalid-state' | 'error';

export function MicrosoftTeamsIntegrationSettings({
  availability,
}: {
  availability?: IntegrationAvailability;
}) {
  const { showToast } = useToast();
  const {
    data: integration,
    isLoading,
    error,
    refetch,
  } = useGetIntegration(IntegrationKey.MicrosoftTeams);
  const beginConsent = useBeginMicrosoftTeamsConsent();
  const testConnection = useTestMicrosoftTeamsConnection();
  const disconnect = useDisconnectMicrosoftTeams();

  const settings = integration?.settings as TeamsSettings | undefined;
  const tenantId = settings?.tenantId;
  const consentedAt = settings?.consentedAt;
  const isConnected = Boolean(tenantId && consentedAt);

  const [connectionStatus, setConnectionStatus] = useState<
    'untested' | 'success' | 'failed'
  >('untested');

  // Surface the consent-callback result piped through the redirect URL as a
  // toast, then strip the query params so a refresh doesn't re-fire it.
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  useEffect(() => {
    const status = search.teamsConsent as ConsentStatus | undefined;
    if (!status) return;
    const errorDescription =
      typeof search.errorDescription === 'string'
        ? search.errorDescription
        : undefined;
    if (status === 'success') {
      setConnectionStatus('untested');
      showToast({
        title: 'Teams Connected',
        message: 'Microsoft Teams admin consent completed.',
        color: 'success',
        icon: 'check',
      });
      void refetch();
    } else {
      showToast({
        title: 'Teams Connection Failed',
        message:
          errorDescription ??
          (status === 'cancelled'
            ? 'Admin consent was cancelled.'
            : status === 'invalid-state'
              ? 'The consent state was invalid or expired. Please try again.'
              : 'Microsoft returned an error during admin consent.'),
        color: 'error',
        icon: 'error',
      });
    }
    void navigate({ to: '.', search: {}, replace: true });
  }, [search, navigate, refetch, showToast]);

  const handleConnect = async () => {
    try {
      const { consentUrl } = await beginConsent.mutateAsync({});
      window.location.assign(consentUrl);
    } catch (err) {
      showToast({
        title: 'Could Not Start Consent',
        message:
          err instanceof Error
            ? err.message
            : 'Unexpected error starting consent.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const handleTestConnection = async () => {
    if (!integration?.id) return;
    try {
      const result = await testConnection.mutateAsync({
        integrationId: integration.id,
      });
      setConnectionStatus(result.success ? 'success' : 'failed');
      showToast({
        title: result.success ? 'Connection Successful' : 'Connection Failed',
        message: result.message,
        color: result.success ? 'success' : 'error',
        icon: result.success ? 'check' : 'error',
      });
    } catch {
      setConnectionStatus('failed');
      showToast({
        title: 'Connection Test Failed',
        message: 'An unexpected error occurred while testing the connection.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const handleDisconnect = async () => {
    if (!integration?.id) return;
    try {
      await disconnect.mutateAsync({ integrationId: integration.id });
      setConnectionStatus('untested');
      showToast({
        title: 'Disconnected',
        message:
          'Microsoft Teams admin consent has been cleared. Reconnect to restore the integration.',
        color: 'success',
        icon: 'check',
      });
    } catch {
      showToast({
        title: 'Disconnect Failed',
        message: 'An unexpected error occurred while disconnecting.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
        <span>
          <T context="loading message while Teams integration settings are fetched">
            Loading integration settings...
          </T>
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <AlertContainer variant="destructive">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertDescription>
          <T context="error message when Teams integration settings fail to load">
            Failed to load integration settings. Please try refreshing the page.
          </T>
        </AlertDescription>
      </AlertContainer>
    );
  }

  const backendUnavailable = availability && !availability.available;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        {isConnected && connectionStatus === 'success' ? (
          <>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="font-medium">
                <T context="Teams integration status label when connection test succeeded">
                  Integration Active
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-green-100 text-green-700 border-green-300"
            >
              <T context="Teams integration badge when successfully connected">
                Connected
              </T>
            </Badge>
          </>
        ) : connectionStatus === 'failed' ? (
          <>
            <div className="flex items-center gap-2">
              <WarningCircleIcon className="h-5 w-5 text-red-500" />
              <span className="font-medium">
                <T context="Teams integration status label when connection test failed">
                  Connection Failed
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-red-50 text-red-700 border-red-300"
            >
              <T context="Teams integration badge prompting user to reconnect after failure">
                Reconnect Required
              </T>
            </Badge>
          </>
        ) : isConnected ? (
          <>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="font-medium">
                <T context="Teams integration status when consent captured but not tested">
                  Admin Consent Captured
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-300"
            >
              <T context="Teams integration badge when consent captured but connection not verified">
                Not Verified
              </T>
            </Badge>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <WarningCircleIcon className="h-5 w-5 text-amber-500" />
              <span className="font-medium">
                <T context="Teams integration status when not yet connected">
                  Not Connected
                </T>
              </span>
            </div>
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-300"
            >
              <T context="Teams integration badge prompting initial admin consent">
                Admin Consent Required
              </T>
            </Badge>
          </>
        )}
      </div>

      {backendUnavailable && (
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertDescription>
            {availability?.reason ?? (
              <T context="generic fallback when Teams backend env is missing">
                Microsoft Teams is not configured on this server.
              </T>
            )}
          </AlertDescription>
        </AlertContainer>
      )}

      {/* Onboarding instructions — open by default until the tenant has consented */}
      <Accordion
        type="single"
        collapsible
        defaultValue={isConnected ? undefined : 'setup'}
      >
        <AccordionItem
          value="setup"
          className="border border-blue-200 rounded-md bg-blue-50 px-4"
        >
          <AccordionTrigger className="py-3 text-blue-900 hover:no-underline">
            <span className="flex items-center gap-2 font-medium">
              <InfoIcon className="h-5 w-5" />
              <T context="Teams onboarding instructions accordion title">
                Per-tenant onboarding — three steps your Microsoft admin needs
                to take
              </T>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-4 text-sm text-blue-900">
              <p>
                <T context="Teams onboarding intro">
                  The integration uses a Gently-owned multi-tenant Azure AD app.
                  A Microsoft tenant admin grants consent once; then every user
                  who'll receive notifications needs the Gently Teams app
                  installed in their tenant.
                </T>
              </p>

              <div>
                <p className="font-semibold mb-2">
                  <T context="Teams onboarding step 1 heading">
                    1. Grant admin consent
                  </T>
                </p>
                <p>
                  <T context="Teams onboarding step 1 description">
                    Click <strong>Connect Teams</strong> below. You'll be
                    redirected to Microsoft to sign in as a tenant admin and
                    approve the Gently app's permissions (
                    <Var>TeamsActivity.Send</Var>, <Var>User.Read.All</Var>).
                    When you click <strong>Accept</strong>, Microsoft sends you
                    back here with the tenant id captured against your org.
                  </T>
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">
                  <T context="Teams onboarding step 2 heading">
                    2. Install the Gently Teams app in your tenant
                  </T>
                </p>
                <p className="mb-2">
                  <T context="Teams onboarding step 2 description">
                    Your Gently rep will provide a Teams app manifest zip
                    (manifest.json + two icons). A Microsoft 365 admin uploads
                    it via one of:
                  </T>
                </p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>
                    <T context="Teams onboarding step 2 path A">
                      <strong>Teams Admin Center</strong> → Apps → Manage apps →{' '}
                      <strong>Upload new app</strong>
                    </T>
                  </li>
                  <li>
                    <T context="Teams onboarding step 2 path B">
                      Their own Teams client → Apps → Manage your apps →{' '}
                      <strong>Upload a custom app</strong>
                    </T>
                  </li>
                </ul>
                <p className="mt-2 text-xs italic">
                  <T context="Teams onboarding step 2 failure mode">
                    If you skip this, every workflow notification fails with{' '}
                    <Var>403 Forbidden</Var> from Microsoft.
                  </T>
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">
                  <T context="Teams onboarding step 3 heading">
                    3. Make sure recipients have the app installed
                  </T>
                </p>
                <p>
                  <T context="Teams onboarding step 3 description">
                    Each user who will receive notifications needs the Gently
                    Teams app installed in some scope (personal works). The
                    easiest path is a tenant-wide app-permission policy that
                    includes Gently — otherwise each user has to install it
                    themselves from <strong>Apps → Manage your apps</strong>.
                  </T>
                </p>
              </div>

              <p className="text-xs italic">
                <T context="Teams onboarding closing line about Test Connection">
                  Once all three steps are done, click{' '}
                  <strong>Test Connection</strong> below — a green "Connection
                  Successful" toast confirms the integration is live.
                </T>
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="recipients"
          className="mt-2 border border-blue-200 rounded-md bg-blue-50 px-4"
        >
          <AccordionTrigger className="py-3 text-blue-900 hover:no-underline">
            <span className="flex items-center gap-2 font-medium">
              <InfoIcon className="h-5 w-5" />
              <T context="Teams recipient requirements accordion title">
                Who can receive Teams notifications
              </T>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2 text-sm text-blue-900">
              <p>
                <T context="Teams recipient requirements intro">
                  Workflow steps resolve recipients by email. For the email →
                  Teams user lookup to succeed, each recipient must:
                </T>
              </p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>
                  <T context="Teams recipient requirement 1 — mailbox">
                    Have a <strong>Microsoft 365 mailbox</strong> in the
                    consenting tenant. External guests don't work.
                  </T>
                </li>
                <li>
                  <T context="Teams recipient requirement 2 — UPN matches">
                    Have a UPN (sign-in email) that matches the email on their
                    ERP user record. Mismatches fail with{' '}
                    <Var>
                      <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                        No Teams user found for email "…"
                      </code>
                    </Var>
                    .
                  </T>
                </li>
                <li>
                  <T context="Teams recipient requirement 3 — app installed">
                    Have the Gently Teams app available in their personal apps
                    list — otherwise Microsoft returns 403 even when the
                    tenant-level consent is in place.
                  </T>
                </li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="troubleshooting"
          className="mt-2 border border-blue-200 rounded-md bg-blue-50 px-4"
        >
          <AccordionTrigger className="py-3 text-blue-900 hover:no-underline">
            <span className="flex items-center gap-2 font-medium">
              <InfoIcon className="h-5 w-5" />
              <T context="Teams troubleshooting accordion title">
                Troubleshooting
              </T>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="text-sm text-blue-900">
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>
                  <T context="Teams troubleshoot AADSTS65001">
                    <strong>
                      Test Connection returns{' '}
                      <Var>
                        <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                          AADSTS65001
                        </code>
                      </Var>
                    </strong>{' '}
                    — admin consent wasn't granted, or it was revoked. Click{' '}
                    <strong>Reconnect</strong> and grant consent again.
                  </T>
                </li>
                <li>
                  <T context="Teams troubleshoot 403 sendActivityNotification">
                    <strong>
                      Workflow step fails with{' '}
                      <Var>
                        <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                          403 Forbidden
                        </code>
                      </Var>
                    </strong>{' '}
                    — the Gently Teams app isn't installed in the tenant or in
                    the recipient's personal scope. Re-check steps 2 and 3
                    above.
                  </T>
                </li>
                <li>
                  <T context="Teams troubleshoot no Teams user">
                    <strong>
                      <Var>
                        <code className="rounded bg-blue-100 px-1 py-0.5 text-xs font-mono">
                          No Teams user found for email "…"
                        </code>
                      </Var>
                    </strong>{' '}
                    — the recipient doesn't have a mailbox in the consenting
                    tenant, or their UPN doesn't match the email on their ERP
                    user record.
                  </T>
                </li>
                <li>
                  <T context="Teams troubleshoot unknown activityType">
                    <strong>
                      Step fails mentioning an unknown activityType
                    </strong>{' '}
                    — the activity type isn't declared in the installed Teams
                    app manifest. Coordinate with Gently to update + re-sideload
                    the manifest.
                  </T>
                </li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <T context="Teams integration settings card title">
              Connection Configuration
            </T>
          </CardTitle>
          <CardDescription>
            <T context="Teams integration settings card description">
              Authorize the Gently app inside your Microsoft tenant so workflows
              can send activity-feed notifications.
            </T>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isConnected ? (
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <div>
                  <span className="text-muted-foreground">
                    <T context="Teams settings field label for tenant id">
                      Tenant ID:
                    </T>
                  </span>{' '}
                  <code className="text-xs">{tenantId}</code>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    <T context="Teams settings field label for consent timestamp">
                      Consented At:
                    </T>
                  </span>{' '}
                  <Var>
                    {consentedAt ? new Date(consentedAt).toLocaleString() : ''}
                  </Var>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                <T context="Teams settings help text describing what the integration does">
                  Workflows can now resolve users in this tenant and send
                  notifications to their Teams activity feed.
                </T>
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              <T context="Teams settings help text before connection">
                Click <Var>Connect Teams</Var> to grant admin consent. You'll be
                redirected to Microsoft and bounced back here once approved.
              </T>
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            {isConnected ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testConnection.isPending}
                >
                  {testConnection.isPending ? (
                    <>
                      <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                      <T context="button label while Teams connection test runs">
                        Testing...
                      </T>
                    </>
                  ) : (
                    <>
                      <PlugIcon className="h-4 w-4 mr-2" />
                      <T context="button label to run Teams connection test">
                        Test Connection
                      </T>
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleConnect}
                  disabled={beginConsent.isPending || backendUnavailable}
                >
                  <T context="button label to re-run Teams admin consent">
                    Reconnect
                  </T>
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDisconnect}
                  disabled={disconnect.isPending}
                >
                  <PlugsIcon className="h-4 w-4 mr-2" />
                  <T context="button label to disconnect the Teams integration">
                    Disconnect
                  </T>
                </Button>
              </>
            ) : (
              <Button
                onClick={handleConnect}
                disabled={beginConsent.isPending || backendUnavailable}
              >
                {beginConsent.isPending ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                    <T context="button label while starting Teams admin consent">
                      Redirecting...
                    </T>
                  </>
                ) : (
                  <T context="primary button label to start Teams admin consent">
                    Connect Teams
                  </T>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
